import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Stack,
  CircularProgress,
  Link
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import DownloadIcon from '@mui/icons-material/Download';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ReactMarkdown from 'react-markdown';
import DocumentInspectionModal, { DocumentInspectionData } from './DocumentInspectionModal';

import {
  BatchGenerationProgress,
  BatchGenerationResult,
  FolderAssignment,
  GenerationStatus
} from '../types';

interface GenerationProgressViewProps {
  progress: BatchGenerationProgress | null;
  result: BatchGenerationResult | null;
  isGenerating: boolean;
  assignments: FolderAssignment[];
  onFetchProfile?: (userId: string) => Promise<string>;
  onFetchDocuments?: (userId: string) => Promise<DocumentInspectionData>;
  onRetry?: (userId: string, options?: { force?: boolean }) => void | Promise<void>;
  onExport?: () => void;
  retryingUserIds?: string[];
  onBulkRegeneratePlaceholders?: () => void | Promise<void>;
  isBulkRegenerating?: boolean;
}

interface TableRowData {
  userId: string;
  userName?: string;
  email?: string;
  status: GenerationStatus;
  documentsProcessed?: number;
  documentsDetails?: Array<{
    name: string;
    path: string;
    extractedText?: string;
    size?: number;
    lastModified?: Date;
    type?: string;
  }>;
  folderPath?: string;
  attempts?: number;
  processingTime?: number;
  isActive: boolean;
  errorMessage?: string;
}

const STATUS_LABELS: Record<GenerationStatus, string> = {
  pending: 'Pending',
  extracting: 'Extracting',
  enhancing: 'Enhancing',
  storing: 'Storing',
  completed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped'
};

const STATUS_COLORS: Record<GenerationStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  pending: 'default',
  extracting: 'info',
  enhancing: 'info',
  storing: 'info',
  completed: 'success',
  failed: 'error',
  skipped: 'warning'
};

const PREVIEWABLE_STATUSES: GenerationStatus[] = ['completed', 'skipped'];

const formatProcessingTime = (seconds?: number) => {
  if (!seconds || seconds <= 0) {
    return '—';
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}m ${remainder}s`;
};

const GenerationProgressView: React.FC<GenerationProgressViewProps> = ({
  progress,
  result,
  isGenerating,
  assignments,
  onFetchProfile,
  onFetchDocuments,
  onRetry,
  onExport,
  retryingUserIds,
  onBulkRegeneratePlaceholders,
  isBulkRegenerating
}) => {
  const [previewUser, setPreviewUser] = useState<{ userId: string; userName?: string } | null>(null);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [documentInspection, setDocumentInspection] = useState<{
    open: boolean;
    data: DocumentInspectionData | null;
    isLoading: boolean;
    error: string | null;
  }>({ open: false, data: null, isLoading: false, error: null });

  const detailMap = useMemo(() => {
    const map = new Map<string, (BatchGenerationResult['details'])[number]>();
    result?.details.forEach((detail) => {
      map.set(detail.userId, detail);
    });
    return map;
  }, [result]);

  const errorMap = useMemo(() => {
    const map = new Map<string, string>();
    progress?.errors.forEach((err) => {
      if (!map.has(err.userId)) {
        map.set(err.userId, err.error);
      }
    });
    return map;
  }, [progress]);

  const rows = useMemo<TableRowData[]>(() => {
    const data: TableRowData[] = assignments
      .filter((assignment) => assignment.isAssigned && assignment.assignedUser)
      .map((assignment) => {
        const assignedUser = assignment.assignedUser!;
        const detail = detailMap.get(assignedUser.userId);
        const status = detail?.status ?? (isGenerating ? 'pending' : 'skipped');
        const isActive = progress?.current?.userId === assignedUser.userId;
        const errorMessage = detail?.error ?? errorMap.get(assignedUser.userId) ?? undefined;

        return {
          userId: assignedUser.userId,
          userName: assignedUser.displayName,
          email: assignedUser.email,
          status,
          documentsProcessed: detail?.documentsProcessed,
          documentsDetails: detail?.documentsDetails,
          folderPath: detail?.folderPath,
          attempts: detail?.attempts,
          processingTime: detail?.processingTime,
          isActive,
          errorMessage
        };
      });

    // Include any details that aren't part of current assignments (e.g., skipped via test mode)
    detailMap.forEach((detail, userId) => {
      const exists = data.some((row) => row.userId === userId);
      if (!exists) {
        data.push({
          userId,
          userName: detail.userName,
          status: detail.status,
          documentsProcessed: detail.documentsProcessed,
          documentsDetails: detail.documentsDetails,
          folderPath: detail.folderPath,
          attempts: detail.attempts,
          processingTime: detail.processingTime,
          isActive: false,
          errorMessage: detail.error
        });
      }
    });

    return data;
  }, [assignments, detailMap, errorMap, isGenerating, progress]);

  const processedCount = progress
    ? progress.completed + progress.failed + progress.skipped
    : result
      ? result.summary.completed + result.summary.failed + result.summary.skipped
      : 0;

  const totalCount = progress?.total ?? result?.summary.total ?? rows.length;
  const progressValue = totalCount > 0 ? Math.min((processedCount / totalCount) * 100, 100) : 0;
  const bulkButtonDisabled = Boolean(isBulkRegenerating) || isGenerating;

  const handleViewProfile = async (row: TableRowData) => {
    if (!onFetchProfile) {
      return;
    }

    try {
      setPreviewUser({ userId: row.userId, userName: row.userName });
      setPreviewContent('');
      setPreviewError(null);
      setIsLoadingPreview(true);
      const markdown = await onFetchProfile(row.userId);
      setPreviewContent(markdown);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load generated profile';
      setPreviewError(message);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewUser(null);
    setPreviewContent('');
    setPreviewError(null);
  };

  const handleViewDocuments = useCallback(async (row: TableRowData) => {
    // If we have inline document details, use them directly
    if (row.documentsDetails && row.documentsDetails.length > 0) {
      setDocumentInspection({
        open: true,
        data: {
          userId: row.userId,
          userName: row.userName,
          folderPath: row.folderPath,
          documents: row.documentsDetails.map(doc => ({
            name: doc.name,
            path: doc.path,
            extractedText: doc.extractedText || '',
            size: doc.size,
            lastModified: doc.lastModified,
            type: doc.type
          })),
          totalDocuments: row.documentsDetails.length
        },
        isLoading: false,
        error: null
      });
      return;
    }

    // Otherwise, fetch from the API if a handler is provided
    if (onFetchDocuments) {
      setDocumentInspection({
        open: true,
        data: null,
        isLoading: true,
        error: null
      });

      try {
        const data = await onFetchDocuments(row.userId);
        setDocumentInspection(prev => ({
          ...prev,
          data,
          isLoading: false
        }));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load document details';
        setDocumentInspection(prev => ({
          ...prev,
          error: message,
          isLoading: false
        }));
      }
    }
  }, [onFetchDocuments]);

  const handleCloseDocumentInspection = () => {
    setDocumentInspection({
      open: false,
      data: null,
      isLoading: false,
      error: null
    });
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6">Generation Progress</Typography>
            <Typography variant="body2" color="text.secondary">
              {processedCount} of {totalCount} profiles processed
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {onBulkRegeneratePlaceholders && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={isBulkRegenerating ? <CircularProgress size={16} /> : <AutorenewIcon fontSize="small" />}
                onClick={onBulkRegeneratePlaceholders}
                disabled={bulkButtonDisabled}
              >
                Regenerate Placeholders
              </Button>
            )}
            {onExport && result && (
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onExport}>
                Export Summary
              </Button>
            )}
          </Stack>
        </Box>

        <Box mb={2}>
          <LinearProgress
            variant={totalCount > 0 ? 'determinate' : 'indeterminate'}
            value={progressValue}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {progress?.errors.length ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {progress.errors.length} issue{progress.errors.length > 1 ? 's' : ''} encountered. Review the table for details.
          </Alert>
        ) : null}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Documents</TableCell>
                <TableCell align="right">Attempts</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No assigned folders awaiting generation.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => {
                const isRetrying = retryingUserIds?.includes(row.userId) ?? false;
                const forceRegeneration = row.status !== 'failed';
                const canPreviewProfile = Boolean(onFetchProfile) && PREVIEWABLE_STATUSES.includes(row.status);
                const previewTooltip = row.status === 'skipped'
                  ? 'View existing profile'
                  : 'View generated profile';

                return (
                  <TableRow key={row.userId} hover selected={row.isActive}>
                    <TableCell>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body2" fontWeight={600}>{row.userName || row.userId}</Typography>
                        {row.email && (
                          <Typography variant="caption" color="text.secondary">{row.email}</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[row.status]}
                        color={STATUS_COLORS[row.status]}
                        size="small"
                        variant={row.isActive ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {row.documentsProcessed ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleViewDocuments(row)}
                          sx={{
                            fontWeight: 500,
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                        >
                          {row.documentsProcessed}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">{row.attempts ?? '—'}</TableCell>
                    <TableCell align="right">{formatProcessingTime(row.processingTime)}</TableCell>
                    <TableCell>
                      {row.errorMessage ? (
                        <Typography variant="caption" color="error">
                          {row.errorMessage}
                        </Typography>
                      ) : row.isActive && isGenerating ? (
                        <Typography variant="caption" color="text.secondary">Processing…</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {canPreviewProfile && (
                          <Tooltip title={previewTooltip}>
                            <span>
                              <IconButton size="small" onClick={() => handleViewProfile(row)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {onRetry && ['failed', 'completed', 'skipped'].includes(row.status) && (
                          <Tooltip title={row.status === 'completed' ? 'Regenerate profile' : 'Retry generation'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => onRetry(row.userId, { force: forceRegeneration })}
                                disabled={isRetrying}
                              >
                                {isRetrying
                                  ? <CircularProgress size={18} />
                                  : <ReplayIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={Boolean(previewUser)} onClose={handleClosePreview} fullWidth maxWidth="md">
        <DialogTitle>
          {previewUser?.userName || previewUser?.userId || 'Generated Profile'}
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 300 }}>
          {isLoadingPreview ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress size={32} />
            </Box>
          ) : previewError ? (
            <Alert severity="error">{previewError}</Alert>
          ) : previewContent ? (
            <Box sx={{ '& h1': { fontSize: '1.5rem' } }}>
              <ReactMarkdown>{previewContent}</ReactMarkdown>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No profile content available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      <DocumentInspectionModal
        open={documentInspection.open}
        onClose={handleCloseDocumentInspection}
        data={documentInspection.data}
        isLoading={documentInspection.isLoading}
        error={documentInspection.error}
      />
    </Box>
  );
};

export default GenerationProgressView;
